import {
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { initializeOnboarding } from "./onboarding";
import { captureException } from "./posthog";

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account",
});

// Use popup for localhost, redirect for production
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

// Helper to capture exceptions to PostHog
function captureError(error: unknown, context?: Record<string, unknown>) {
  console.error("[Auth]", error);
  captureException(error, context);
}

// auth/network-request-failed is caused by user connectivity (laptop sleep/wake,
// flaky wifi, captive portals) — not an app bug. The session-refresh path in
// AuthContext.tsx already swallows it; do the same on the user-initiated sign-in
// paths so it doesn't flood the exception dashboard. We still re-throw so the
// UI can surface a "check your connection" message.
function isTransientNetworkError(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  return code === "auth/network-request-failed";
}

export async function updateUserProfile(user: User) {
  try {
    const userRef = doc(db, "users", user.uid);
    await setDoc(
      userRef,
      {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    console.log("[Auth] User profile updated", { userId: user.uid });
  } catch (error) {
    captureError(error, { operation: "update_user_profile", userId: user.uid });
    throw error;
  }
}

// Transient Firebase auth network failures are the largest remaining auth
// error in PostHog (LB-199 / LB-177 / LB-192). One retry after a short delay
// clears the vast majority — they're caused by a momentarily-offline tab or a
// flaky network handshake to securetoken.googleapis.com, not a real failure.
const AUTH_NETWORK_RETRY_DELAY_MS = 1_500;

async function signInWithPopupResilient(provider: GoogleAuthProvider) {
  try {
    return await signInWithPopup(auth, provider);
  } catch (error: any) {
    if (error?.code !== "auth/network-request-failed") throw error;
    console.warn(
      "[Auth] signInWithPopup hit auth/network-request-failed; retrying once after",
      AUTH_NETWORK_RETRY_DELAY_MS,
      "ms",
    );
    await new Promise((resolve) => setTimeout(resolve, AUTH_NETWORK_RETRY_DELAY_MS));
    return await signInWithPopup(auth, provider);
  }
}

export async function signInWithGoogle() {
  try {
    // Ensure persistence is set to local storage
    await setPersistence(auth, browserLocalPersistence);

    // Use popup for all environments - works better with custom domains
    console.log("[Auth] Starting Google sign-in popup");

    const result = await signInWithPopupResilient(googleProvider);

    if (result?.user) {
      // Create session cookie immediately after successful sign-in
      try {
        const idToken = await result.user.getIdToken();
        await fetch("/api/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        console.log("[Auth] Session cookie created after sign-in");
      } catch (sessionError) {
        console.error("[Auth] Failed to create session cookie:", sessionError);
      }

      await updateUserProfile(result.user);
    }

    return result;
  } catch (error: any) {
    const errorCode = error?.code || "unknown";
    const errorMessage = error?.message || "Unknown error during sign-in";

    // Don't capture user-cancellation as an exception — it's expected behavior, not an error
    const userCancelled =
      errorCode === "auth/popup-closed-by-user" ||
      errorCode === "auth/cancelled-popup-request" ||
      errorCode === "auth/user-cancelled";

    // Safari/Firefox routinely block third-party popups even on a click —
    // when that happens, transparently fall back to the redirect flow. This
    // is the single largest source of "FirebaseError" we see in PostHog.
    if (errorCode === "auth/popup-blocked") {
      console.log("[Auth] Popup blocked, falling back to redirect");
      try {
        await signInWithRedirect(auth, googleProvider);
        return null; // The page will navigate away; result handled in handleRedirectResult
      } catch (redirectError) {
        captureError(redirectError as Error, {
          operation: "google_sign_in_redirect_fallback",
        });
        throw redirectError;
      }
    }

    if (userCancelled) {
      console.log("[Auth] Google sign-in cancelled by user", { errorCode });
    } else if (isTransientNetworkError(error)) {
      console.warn("[Auth] Google sign-in network error — likely offline", { errorCode });
    } else {
      captureError(error, {
        operation: "google_sign_in",
        error_code: errorCode,
        errorMessage,
      });
    }

    throw error;
  }
}

export async function handleRedirectResult() {
  console.log("[Auth] handleRedirectResult called", {
    isLocalhost,
    currentUrl: typeof window !== "undefined" ? window.location.href : "server",
  });

  try {
    const result = await getRedirectResult(auth);
    if (!result?.user) {
      console.log("[Auth] No redirect result pending");
      return null;
    }

    // Mirror the session-cookie + profile-update step the popup path takes,
    // so a successful redirect lands the user in the same state as a popup.
    try {
      const idToken = await result.user.getIdToken();
      await fetch("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
    } catch (sessionError) {
      console.error("[Auth] Failed to create session cookie after redirect:", sessionError);
    }

    await updateUserProfile(result.user);
    return result;
  } catch (error) {
    console.error("[Auth] handleRedirectResult error:", error);
    return null;
  }
}

export function onAuthChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

export async function signOut() {
  try {
    console.log("[Auth] User signing out");
    await firebaseSignOut(auth);
    console.log("[Auth] Sign out successful");
  } catch (error) {
    captureError(error, { operation: "sign_out" });
    throw error;
  }
}

export function getCurrentUser() {
  return auth.currentUser;
}

// Development-only email/password authentication (localhost and staging only)
export async function signInWithEmail(email: string, password: string) {
  // Only allow on localhost and staging (for testing)
  if (
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1" &&
    !window.location.hostname.includes("staging")
  ) {
    throw new Error(
      "Email/password authentication is only available on localhost and staging"
    );
  }

  try {
    await setPersistence(auth, browserLocalPersistence);
    console.log("[Auth] Starting email/password sign-in");

    const result = await signInWithEmailAndPassword(auth, email, password);

    if (result?.user) {
      // Create session cookie immediately after successful sign-in
      try {
        const idToken = await result.user.getIdToken();
        await fetch("/api/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        console.log("[Auth] Session cookie created after email sign-in");
      } catch (sessionError) {
        console.error("[Auth] Failed to create session cookie:", sessionError);
      }

      await updateUserProfile(result.user);
    }

    return result;
  } catch (error: any) {
    const errorCode = error?.code || "unknown";
    const errorMessage = error?.message || "Unknown error during email sign-in";

    if (isTransientNetworkError(error)) {
      console.warn("[Auth] Email sign-in network error — likely offline", { errorCode });
    } else {
      captureError(error, {
        operation: "email_sign_in",
        error_code: errorCode,
        errorMessage,
      });
    }

    throw error;
  }
}

export async function createUserWithEmail(
  email: string,
  password: string,
  displayName: string
) {
  console.log("[Auth] createUserWithEmail called", {
    email,
    displayName,
    hostname:
      typeof window !== "undefined" ? window.location.hostname : "server",
  });

  // Only allow on localhost and staging (for testing)
  if (
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1" &&
    !window.location.hostname.includes("staging")
  ) {
    console.error(
      "[Auth] Email auth not allowed on this hostname:",
      window.location.hostname
    );
    throw new Error(
      "Email/password authentication is only available on localhost and staging"
    );
  }

  try {
    console.log("[Auth] Setting persistence to local...");
    await setPersistence(auth, browserLocalPersistence);

    console.log("[Auth] Calling createUserWithEmailAndPassword...");
    const result = await createUserWithEmailAndPassword(auth, email, password);
    console.log("[Auth] User created successfully", {
      uid: result?.user?.uid,
      email: result?.user?.email,
    });

    if (result?.user) {
      // Update the user's display name
      console.log("[Auth] Creating user document in Firestore...");
      const userRef = doc(db, "users", result.user.uid);
      await setDoc(
        userRef,
        {
          uid: result.user.uid,
          email: result.user.email,
          displayName: displayName,
          photoURL: null,
          lastLogin: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log("[Auth] User document created");

      // Initialize onboarding for new users
      try {
        console.log("[Auth] Initializing onboarding...");
        await initializeOnboarding(result.user.uid);
        console.log("[Auth] Onboarding initialized");
      } catch (error) {
        console.error("[Auth] Failed to initialize onboarding:", error);
        // Don't fail signup if onboarding initialization fails
      }

      // Create session cookie
      try {
        console.log("[Auth] Creating session cookie...");
        const idToken = await result.user.getIdToken();
        const response = await fetch("/api/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        console.log(
          "[Auth] Session cookie response:",
          response.status,
          response.statusText
        );
      } catch (sessionError) {
        console.error("[Auth] Failed to create session cookie:", sessionError);
      }
    }

    console.log("[Auth] createUserWithEmail completed successfully");
    return result;
  } catch (error: any) {
    const errorCode = error?.code || "unknown";
    const errorMessage = error?.message || "Unknown error during user creation";

    if (isTransientNetworkError(error)) {
      console.warn("[Auth] User creation network error — likely offline", { errorCode });
    } else {
      captureError(error, {
        operation: "create_user_email",
        error_code: errorCode,
        errorMessage,
      });
    }

    throw error;
  }
}

// Get the functions URL based on the Firebase project
function getFunctionsBaseUrl(): string {
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  return `https://us-central1-${projectId}.cloudfunctions.net`;
}

// Magic link authentication (for production)
export async function sendMagicLinkEmail(email: string, redirectUrl?: string) {
  try {
    console.log("[Auth] Sending magic link email", { email });

    const response = await fetch(`${getFunctionsBaseUrl()}/sendMagicLink`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: { email, redirectUrl } }),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.error) {
      const errorMessage =
        responseData.error?.message || "Failed to send magic link";
      throw new Error(errorMessage);
    }

    console.log("[Auth] Magic link sent successfully");
    return responseData.result;
  } catch (error: any) {
    const errorCode = error?.code || "unknown";
    const errorMessage = error?.message || "Unknown error sending magic link";

    console.error("[Auth] Error sending magic link:", errorCode, errorMessage);

    if (isTransientNetworkError(error)) {
      console.warn("[Auth] Magic link send network error — likely offline", { errorCode });
    } else {
      captureError(error, {
        operation: "send_magic_link",
        error_code: errorCode,
        errorMessage,
      });
    }

    throw error;
  }
}

export async function signInWithMagicLink(token: string, id: string) {
  try {
    console.log("[Auth] Verifying magic link");

    // Verify the magic link and get custom token
    const response = await fetch(`${getFunctionsBaseUrl()}/verifyMagicLink`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: { token, id } }),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.error) {
      const errorMessage =
        responseData.error?.message || "Failed to verify magic link";
      throw new Error(errorMessage);
    }

    const data = responseData.result as {
      success: boolean;
      customToken: string;
      email: string;
      uid: string;
      redirectUrl?: string;
    };

    if (!data.success || !data.customToken) {
      throw new Error("Invalid magic link response");
    }

    console.log("[Auth] Magic link verified, signing in with custom token");

    // Sign in with the custom token
    await setPersistence(auth, browserLocalPersistence);
    const userCredential = await signInWithCustomToken(auth, data.customToken);

    if (userCredential?.user) {
      // Create session cookie
      try {
        const idToken = await userCredential.user.getIdToken();
        await fetch("/api/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        console.log("[Auth] Session cookie created after magic link sign-in");
      } catch (sessionError) {
        console.error("[Auth] Failed to create session cookie:", sessionError);
      }
    }

    console.log("[Auth] Magic link sign-in successful");

    return {
      user: userCredential.user,
      redirectUrl: data.redirectUrl,
    };
  } catch (error: any) {
    const errorCode = error?.code || "unknown";
    const errorMessage =
      error?.message || "Unknown error during magic link sign-in";

    console.error(
      "[Auth] Error with magic link sign-in:",
      errorCode,
      errorMessage
    );

    if (isTransientNetworkError(error)) {
      console.warn("[Auth] Magic link sign-in network error — likely offline", { errorCode });
    } else {
      captureError(error, {
        operation: "magic_link_sign_in",
        error_code: errorCode,
        errorMessage,
      });
    }

    throw error;
  }
}
